import { ApolloError, gql, useApolloClient } from "@apollo/client";
import {
  AppSessionCellId,
  BaseCalcType,
  CalcColumnContext,
  CalcColumnDefinition,
  CalcScopeItemTypeSubset,
  CalcsDefinition,
  CellId,
  DisplayTableColumnId,
  DisplayTableColumnOutputType,
  EMPTY_CALCS,
  ExploreCellId,
  ExploreFieldId,
  ExploreSpec,
  HexVersionAtomicOperation,
  HqlAggregationFunction,
  UPDATE_COLUMN_PROPERTIES,
  UPDATE_DISPLAY_TABLE_CONFIG,
  UPDATE_EXPLORE_CELL,
  UpdateColumnPropertiesSafeFields,
  assertNever,
  exploreSpecActions,
  exploreSpecReducer,
  generateColumnIdForField,
  getAllNodesOfType,
  isCalcErrorFatal,
  normalizeCalcsDefinition,
  notEmpty,
  parseCalcExpression,
  setDifference,
  stableEmptyArray,
  stableEmptyObject,
  updateCalcColumnReferences,
  validateAllCalcsFromText,
} from "@hex/common";
import { castDraft, produce } from "immer";
import { difference, isEqual, uniqBy } from "lodash";
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { shallowEqual } from "../../../../util/index.js";

import { useScopeGetter } from "../../../../appsession-multiplayer/state-hooks/scopeStateHooks.js";
import { getUpdateExploreSpecAOs } from "../../../../explore-v2/hooks/useUpdateExploreSpec.js";
import { getDisplayTableConfigFromCellContents } from "../../../../hex-version-multiplayer/handlers/displayTableConfigClientHandlers.js";
import { DisplayTableConfigFragment } from "../../../../hex-version-multiplayer/HexVersionMPModel.generated.js";
import {
  useCellContentsGetter,
  useCellContentsSelector,
} from "../../../../hex-version-multiplayer/state-hooks/cellContentsStateHooks.js";
import { makeUndoToast } from "../../../../hex-version-multiplayer/undoToast.js";
import { ScopeItemMP } from "../../../../redux/slices/appSessionMPSlice.js";
import { useHexVersionAOContext } from "../../../../util/hexVersionAOContext.js";
import { logErrorMsg } from "../../../../util/logging.js";
import type { CalcEditorAPI } from "../../../calcs/CalcEditor.js";
import { ParsedDisplayTableContents } from "../../../cell/renderers/display-table/useParsedDisplayTableOutputContents.js";
import { SetStaleCallback } from "../../../cell/utils/useSetStale.js";
import { useToaster } from "../../../common/Toasts.js";
import type { DisplayTableAPI } from "../ag-grid-table/DisplayTable.js";

import {
  CalcPreviewDocument,
  CalcPreviewQuery,
  CalcPreviewQueryVariables,
} from "./TableCalcContext.generated.js";

export type CalcInsertType = "left" | "right";

export type CalcPreviewData = {
  previewData: readonly string[] | string;
  resultType: DisplayTableColumnOutputType;
};

const NO_PREVIEW_DATA: CalcPreviewData = {
  previewData: "",
  resultType: "STRING",
};

const INVALID_FORMULA_PREVIEW_DATA: CalcPreviewData = {
  previewData: "Invalid formula",
  resultType: "STRING",
};

const getMissingParamData = (paramName: string): CalcPreviewData => ({
  previewData: `"${paramName}" not found`,
  resultType: "STRING",
});

const getWrongTypeParamData = (paramName: string): CalcPreviewData => ({
  previewData: `"${paramName}" has wrong type`,
  resultType: "STRING",
});

const getAggPreviewData = (aggUsed: string): CalcPreviewData => ({
  previewData: `"${aggUsed}" not previewable`,
  resultType: "STRING",
});

gql`
  query CalcPreview(
    $expr: String!
    $appSessionCellId: AppSessionCellId!
    $paramValues: [CalcPreviewParameterValue!]!
  ) {
    calcPreview(
      expr: $expr
      appSessionCellId: $appSessionCellId
      paramValues: $paramValues
    ) {
      previewData
      resultType
    }
  }
`;

function getReferencedTypesFromCalcs(
  newCalcColumns: readonly CalcColumnDefinition[],
  calcColumnContext: CalcColumnContext,
  scope: { [name: string]: ScopeItemMP | undefined },
  prevReferencedParamTypes: CalcsDefinition["referencedParamTypes"],
): Pick<
  Required<CalcsDefinition>,
  "referencedColumns" | "referencedParamTypes"
> {
  const allASTs = newCalcColumns.map((c) => parseCalcExpression(c.expression));

  // Columns
  const allColumnRefs = allASTs.flatMap((ast) =>
    getAllNodesOfType(ast, "column"),
  );
  const uniqColumnRefs = uniqBy(allColumnRefs, (c) => c.name);
  const referencedColumns: CalcsDefinition["referencedColumns"] = uniqColumnRefs
    .map((col) => {
      const colId = DisplayTableColumnId.check(col.name);
      const column = calcColumnContext.find((c) => c.columnId === colId);
      return (
        column && {
          ...column,
          containsAggregate: column?.containsAggregate ?? false,
        }
      );
    })
    .filter(notEmpty);

  // Params
  const allParamRefs = allASTs.flatMap((ast) =>
    getAllNodesOfType(ast, "parameterReference"),
  );
  const uniqParamRefs = uniqBy(allParamRefs, (c) => c.name);

  const referencedParamTypes = uniqParamRefs.reduce(
    (acc, param) => {
      const maybeScopeItemType = scope?.[param.name]?.type;
      const scopeItemType = CalcScopeItemTypeSubset.guard(maybeScopeItemType)
        ? maybeScopeItemType
        : undefined;

      const prevType = prevReferencedParamTypes?.[param.name];

      // Scope item types are likely to fluctuate as a project kernel is started/runs.
      // So if the type of a referenced param isn't currently available or isn't something expected,
      // we keep using the last known type.
      const typeToUse = scopeItemType ?? prevType;

      if (typeToUse != null) {
        acc[param.name] = typeToUse;
      }
      return acc;
    },
    {} as NonNullable<CalcsDefinition["referencedParamTypes"]>,
  );

  return { referencedColumns, referencedParamTypes };
}

function maybeGetCalcNameError(
  newCalcColumns: readonly CalcColumnDefinition[],
  parsedTableOutput: ParsedDisplayTableContents | null,
): string | undefined {
  const names: Set<string> = new Set();
  for (const col of newCalcColumns) {
    if (names.has(col.name)) {
      return `A calculation column with name "${col.name}" already exists`;
    }
    if (col.name === "") {
      return `A calculation column cannot have an empty name`;
    }
    names.add(col.name);
  }

  if (parsedTableOutput?.type !== "table") return;

  const contents = parsedTableOutput.parsedContents;

  const allColumnIds = new Set(contents.columns.map((c) => c.columnId));
  const calcIdsWhenRun = new Set(
    contents.calcColumns?.map((c) => c.name) ?? [],
  );
  const underlyingColumnIds = setDifference(allColumnIds, calcIdsWhenRun);

  for (const col of newCalcColumns) {
    if (underlyingColumnIds.has(col.name)) {
      return `The underlying table already has a column with name "${col.name}"`;
    }
  }
}

interface InProgressExpression {
  expr: string;
  isValid: boolean;
  /** The name of an aggregate function used in the expression, if any */
  aggUsed?: string;
  /** A calc might not directly use an agg, but depend on a measure or calc that does */
  hasAgg?: boolean;
  /** The names of any parameters referenced in the expression, if any */
  paramsUsed?: Set<string>;
  calcType?: BaseCalcType;
}

export interface StartEditingNewCalcOptions {
  insertType?: CalcInsertType;
  targetColumnId?: DisplayTableColumnId;
  initialExpression?: string;
  // User clicked "New measure" in explore, intends to write a measure (agg calc)
  isExploreMeasure?: boolean;
  initialCalcName?: string;
  fieldsToReplaceWithCalc?: ExploreFieldId[] | undefined;
}

export interface TableCalcsContext {
  /** The current set of calc columns. */
  calcColumns: readonly CalcColumnDefinition[];
  /** The last saved set of referenced param types.
   * This does _not_ represent the current scope of possible parameters types,
   * only the ones currently used. */
  referencedParamTypes: CalcsDefinition["referencedParamTypes"];

  /** The total set of referenceable columns available for reference in calcs. */
  calcColumnContext: CalcColumnContext;

  /** The current column which should have its formula shown in the calc editor bar. */
  calcColumnToEdit: DisplayTableColumnId | undefined;

  /**
   * Used to keep track of whether the rename input is open or not for a calc column
   * to prevent deleting calc when the rename input is open
   */
  calcRenameInputOpen: boolean;

  startEditingNewCalc: (opts?: StartEditingNewCalcOptions) => void;
  startEditingExistingCalc: (columnId: DisplayTableColumnId) => void;

  /** Whether a calc is currently being edited */
  isEditingCalc: boolean;
  /** Whether the given column is the calc currently being edited */
  getIsEditingCalcColumn: (columnId: DisplayTableColumnId) => boolean;

  setCalcRenameInputOpen: (open: boolean) => void;

  /** A set of columns (ids) that are referenced by the calc currently being edited. */
  currentlyReferencedColumns: ReadonlySet<string>;
  setCurrentlyReferencedColumns: (
    referencedColumnIds: readonly string[],
  ) => void;

  cancelEditingCalc: () => void;

  columnClicked: (columnId: DisplayTableColumnId) => void;
  totalClicked: (
    columnId: DisplayTableColumnId,
    agg: HqlAggregationFunction,
  ) => void;

  /**
   * Updates this context with the current in-progress expression,
   * for the purpose of making BE calls to generate preview data.
   */
  setInProgressExpression: (expr: InProgressExpression) => void;
  /**
   * Preview data for the last in-progress expression.
   * If `previewData.data` is a scalar, the same value can be used for every row.
   */
  previewData: CalcPreviewData | undefined;

  /**
   * Components other than `CalcEditorBar` should _not_ directly access this
   * and interactions with the editor should be made by calling context methods.
   * This ref object will be a stable outer reference for the lifetime
   * of the context, so is safe to add to dep arrays if ESLint makes you.
   */
  editorAPIRef: React.MutableRefObject<CalcEditorAPI | null>;

  /**
   * Components other than `DisplayTable` should _not_ directly access this
   * and interactions with the editor should be made by calling context methods.
   * This ref object will be a stable outer reference for the lifetime
   * of the context, so is safe to add to dep arrays if ESLint makes you.
   */
  tableAPIRef: React.MutableRefObject<DisplayTableAPI | null>;

  /** saves info for the calc currently being edited. returns false if there were errors */
  updateCalc: (
    newColumnId: DisplayTableColumnId,
    expression: string,
    opts?: {
      closeEditor?: boolean;
      runCell?: boolean;
      scrollToCalc?: boolean;
      extraAOsAfterUpdate?: HexVersionAtomicOperation[];
    },
  ) => boolean;
  updateCalcName: (
    oldColumnId: DisplayTableColumnId,
    newColumnId: string,
    runCell?: boolean,
  ) => void;
  deleteCalc: (columnId: DisplayTableColumnId, runCell?: boolean) => void;

  initialExpression: undefined | string;

  setIsInProgressCalcValid: (isValid: boolean) => void;
  isInProgressCalcValid: boolean;
  isAuthoringExploreMeasure: boolean;
  inProgressCalcType: BaseCalcType;
  doesInProgressCalcHaveAgg: boolean;

  /**
   * If the user started writing a calc e.g. to replace a field in a chart
   */
  fieldsToReplaceWithCalc: ExploreFieldId[] | undefined;
}

const Context = createContext<TableCalcsContext | null>(null);

export interface TableCalcsContextProviderProps {
  appSessionCellId: AppSessionCellId | undefined;
  cellId: CellId;
  setStale: SetStaleCallback;

  parsedTableOutput: ParsedDisplayTableContents | null;

  /**
   * If set, this will be the set of columns that can be referenced in calcs,
   * which is good when the set of columns can be known ahead of time, such as
   * in Explore, where the field-set is already precomputed.
   *
   * If unset, this will be derived from `parsedTableOutput`, which is the
   * default fallback and suitable for display tables and other cases where
   * the only source of truth for the set of columns is the last table output.
   */
  explicitCalcColumnContext?: CalcColumnContext;

  skipDeletedToast?: boolean;
}

export const TableCalcsContextProvider: React.ComponentType<
  React.PropsWithChildren<TableCalcsContextProviderProps>
> =
  // eslint-disable-next-line max-lines-per-function
  React.memo(function TableCalcsContextProvider({
    appSessionCellId,
    cellId,
    children,
    explicitCalcColumnContext: calcColumnContextProp,
    parsedTableOutput,
    setStale,
    skipDeletedToast,
  }) {
    const client = useApolloClient();
    const toaster = useToaster();

    const editorAPIRef = React.useRef<CalcEditorAPI | null>(null);
    const tableAPIRef = React.useRef<DisplayTableAPI | null>(null);

    const { dispatchAO, undoAO } = useHexVersionAOContext();

    const getExploreSpec = useCellContentsGetter({
      selector: (contents) => {
        if (contents.__typename !== "ExploreCell") {
          return {
            exploreSpec: null,
            exploreCellId: null,
          };
        }

        return {
          exploreSpec: contents.spec,
          exploreCellId: contents.exploreCellId,
          resultVariables: contents.resultVariables,
        };
      },
    });

    const getDisplayTableConfig = useCellContentsGetter({
      selector: getDisplayTableConfigFromCellContents,
    });
    const { calcs } = useCellContentsSelector({
      cellId,
      equalityFn: shallowEqual,
      selector: (cellContents) => {
        return {
          calcs: getDisplayTableConfigFromCellContents(cellContents)?.calcs,
        };
      },
    });
    const scopeGetter = useScopeGetter();

    const calcColumns = calcs?.calcColumns ?? stableEmptyArray();
    const referencedParamTypes =
      calcs?.referencedParamTypes ?? stableEmptyObject();

    const [calcColumnToEditState, setCalcColumnToEditState] = useState<
      DisplayTableColumnId | undefined
    >();
    const calcColumnToEditRef = useRef(calcColumnToEditState);
    const setCalcColumnToEdit = useCallback(
      (newCalcColumnToEdit: DisplayTableColumnId | undefined) => {
        calcColumnToEditRef.current = newCalcColumnToEdit;
        setCalcColumnToEditState(newCalcColumnToEdit);
      },
      [],
    );

    const isEditingCalc = calcColumnToEditState != null;
    const getIsEditingCalcColumn = useCallback(
      (columnId: DisplayTableColumnId) => {
        return calcColumnToEditState === columnId;
      },
      [calcColumnToEditState],
    );

    const [isInProgressCalcValid, setIsInProgressCalcValid] = useState(true);
    const [calcRenameInputOpen, setCalcRenameInputOpen_] = useState(false);

    // Messy but better than adding another context
    const [isAuthoringExploreMeasure, setisAuthoringExploreMeasure] =
      useState(false);

    const [currentlyReferencedColumns_, setCurrentlyReferencedColumns_] =
      useState<readonly string[]>([]);
    const currentlyReferencedColumns = useMemo<ReadonlySet<string>>(
      () => new Set(currentlyReferencedColumns_),
      [currentlyReferencedColumns_],
    );

    const [previewData, setPreviewData] = useState<
      CalcPreviewData | undefined
    >();
    const previewAbortController = useRef<AbortController | null>(null);

    const calcColumnIds = useMemo(
      () => new Set(calcs?.calcColumns.map((c) => c.name) ?? []),
      [calcs?.calcColumns],
    );

    const calcColumnContext = useMemo<CalcColumnContext>(() => {
      if (calcColumnContextProp) return calcColumnContextProp;
      if (parsedTableOutput?.type !== "table") return [];
      const columns = parsedTableOutput.parsedContents.columns;
      return columns
        .filter((c) => !calcColumnIds.has(c.columnId))
        .map((c) => ({
          columnId: c.columnId,
          columnType: c.columnType,
          containsAggregate: false,
        }));
    }, [calcColumnContextProp, parsedTableOutput, calcColumnIds]);

    const [initialExpressionState, setInitialExpressionState] = useState<
      string | undefined
    >();

    const clearAndCancelPreview = useCallback(() => {
      setPreviewData(undefined);
      previewAbortController.current?.abort();
      previewAbortController.current = null;
    }, []);

    const [inProgressCalcType, setInProgressCalcType] = useState<BaseCalcType>(
      BaseCalcType.UNKNOWN,
    );
    const [doesInProgressCalcHaveAgg, setDoesInProgressCalcHaveAgg] =
      useState(false);

    const cancelEditingCalc = useCallback(() => {
      setCalcColumnToEdit(undefined);
      setInitialExpressionState(undefined);
      setCurrentlyReferencedColumns_(stableEmptyArray());
      clearAndCancelPreview();
      setFieldsToReplaceWithCalc(undefined);
      setInProgressCalcType(BaseCalcType.UNKNOWN);
      setDoesInProgressCalcHaveAgg(false);
    }, [clearAndCancelPreview, setCalcColumnToEdit]);

    const recomputeCalcTypeAndAgg = useCallback(
      (columnId: DisplayTableColumnId) => {
        if (!columnId) {
          return;
        }
        const calc = calcs?.calcColumns.find((c) => c.name === columnId);
        if (calc == null) {
          return;
        }
        const { resultTypes } = validateAllCalcsFromText(
          new Map(calcs?.calcColumns.map((c) => [c.name, c.expression])) ?? {},
          calcColumnContext,
          {},
          // TODO: customize
          { aggregationMode: "groupable" },
        );
        setInProgressCalcType(
          resultTypes.get(columnId)?.type ?? BaseCalcType.UNKNOWN,
        );
        setDoesInProgressCalcHaveAgg(
          resultTypes.get(columnId)?.containsAggregate ?? false,
        );
      },
      [calcs, calcColumnContext],
    );

    const startEditingExistingCalc = useCallback(
      (columnId: DisplayTableColumnId) => {
        setCalcColumnToEdit(columnId);
        recomputeCalcTypeAndAgg(columnId);
        clearAndCancelPreview();
      },
      [clearAndCancelPreview, recomputeCalcTypeAndAgg, setCalcColumnToEdit],
    );

    const setCurrentlyReferencedColumns = useCallback(
      (columns: readonly string[]) => {
        setCurrentlyReferencedColumns_((prev) =>
          isEqual(prev, columns) ? prev : columns,
        );
      },
      [],
    );

    const setInProgressExpression: TableCalcsContext["setInProgressExpression"] =
      useCallback(
        async ({ aggUsed, calcType, expr, hasAgg, isValid, paramsUsed }) => {
          setInProgressCalcType(calcType ?? BaseCalcType.UNKNOWN);
          setDoesInProgressCalcHaveAgg(hasAgg ?? false);

          if (appSessionCellId == null) return;

          if (expr === "") {
            clearAndCancelPreview();
            setPreviewData(NO_PREVIEW_DATA);
            return;
          }

          if (!isValid) {
            clearAndCancelPreview();
            setPreviewData(INVALID_FORMULA_PREVIEW_DATA);
            return;
          }

          if (aggUsed != null) {
            clearAndCancelPreview();
            setPreviewData(getAggPreviewData(aggUsed));
            return;
          }

          // If the formula is the same as the currently saved calc, we don't need a preview
          const noCalcChange = calcColumns.some(
            (c) =>
              c.name === calcColumnToEditRef.current && c.expression === expr,
          );
          if (noCalcChange) {
            clearAndCancelPreview();
            return;
          }

          // Get values of params used in the expression from scope (if possible)
          const paramValues = [];
          if (paramsUsed != null && paramsUsed.size > 0) {
            const scope = scopeGetter();

            for (const paramName of paramsUsed) {
              const scopeItem = scope[paramName];

              if (scopeItem == null) {
                clearAndCancelPreview();
                setPreviewData(getMissingParamData(paramName));
                return;
              }

              if (!CalcScopeItemTypeSubset.guard(scopeItem.type)) {
                clearAndCancelPreview();
                setPreviewData(getWrongTypeParamData(paramName));
                return;
              }

              paramValues.push({
                name: paramName,
                type: scopeItem.type,
                value: scopeItem.displayValue,
              });
            }
          }

          // If a previous request was in-flight,
          // clear it so we avoid out-of-order results
          previewAbortController.current?.abort();

          try {
            previewAbortController.current = new AbortController();

            const resp = await client.query<
              CalcPreviewQuery,
              CalcPreviewQueryVariables
            >({
              query: CalcPreviewDocument,
              fetchPolicy: "no-cache",
              context: {
                fetchOptions: {
                  signal: previewAbortController.current.signal,
                },
              },
              variables: {
                expr,
                appSessionCellId,
                paramValues,
              },
            });

            previewAbortController.current = null;
            setPreviewData(resp.data.calcPreview);
          } catch (e) {
            const rootErr = e instanceof ApolloError ? e.networkError : e;
            // Intentionally cancelled request, nothing we need to do
            if (
              rootErr instanceof DOMException &&
              rootErr.name === "AbortError"
            ) {
              return;
            }

            logErrorMsg(e, "Error generating preview for calc.");
            // Reverting back to the original data might look strange,
            // but we also don't have any preview data to show, so just blank everything out
            // in the case of an error
            setPreviewData(NO_PREVIEW_DATA);
          }
        },
        [
          appSessionCellId,
          calcColumns,
          clearAndCancelPreview,
          client,
          scopeGetter,
        ],
      );

    const startEditingNewCalc = useCallback(
      (options: undefined | StartEditingNewCalcOptions) => {
        const {
          fieldsToReplaceWithCalc,
          initialCalcName,
          initialExpression: initialExpressionParam,
          insertType,
          isExploreMeasure,
          targetColumnId,
        } = options ?? {};
        const nonCalcColumnIds = calcColumnContext.map((c) => c.columnId);
        const allIds = [...nonCalcColumnIds, ...calcColumnIds];
        const nums = allIds
          .map((colId) => colId.match(/^formula_(\d+)$/)?.[1])
          .filter(notEmpty)
          .map(Number);
        const maxNum = Math.max(...nums, 0);
        const calcNameParamAlreadyInUse = calcColumnIds.has(
          initialCalcName as DisplayTableColumnId,
        );

        const newColumnName = DisplayTableColumnId.check(
          initialCalcName && !calcNameParamAlreadyInUse
            ? initialCalcName
            : `formula_${maxNum + 1}`,
        );
        setCalcColumnToEdit(newColumnName);
        setInitialExpressionState(initialExpressionParam);
        if (initialExpressionParam) {
          const { errors, resultType } = getTypeAndErrorOfDraftCalc(
            calcs?.calcColumns ?? [],
            calcColumnContext,
            initialExpressionParam,
            newColumnName,
          );
          setInProgressExpression({
            expr: initialExpressionParam,
            isValid: errors?.some(isCalcErrorFatal) ?? true,
            hasAgg: resultType?.containsAggregate ?? false,
            calcType: resultType?.type ?? BaseCalcType.UNKNOWN,
          });
        }
        setisAuthoringExploreMeasure(isExploreMeasure ?? false);
        setFieldsToReplaceWithCalc(fieldsToReplaceWithCalc);
        clearAndCancelPreview();

        const displayTableConfig = getDisplayTableConfig(cellId);
        const customColumnOrdering = displayTableConfig?.customColumnOrdering;
        if (insertType == null || targetColumnId == null) {
          // if inserting calc at end of table, no need to update the column ordering
          return;
        }

        // if new columns have been added they may not be in the custom column
        // ordering, so we need to add them at the end
        const orderedColumnIds = customColumnOrdering
          ? [...customColumnOrdering, difference(allIds, customColumnOrdering)]
          : allIds;

        const idx = orderedColumnIds.findIndex((col) => col === targetColumnId);
        if (idx === -1) {
          return;
        }
        orderedColumnIds.splice(
          insertType === "left" ? idx : idx + 1,
          0,
          newColumnName,
        );

        if (displayTableConfig) {
          dispatchAO(
            UPDATE_DISPLAY_TABLE_CONFIG.create({
              displayTableConfigId: displayTableConfig.id,
              cellId,
              key: "customColumnOrdering",
              value: orderedColumnIds as DisplayTableColumnId[],
              suppressAutoRun: true,
            }),
          );
        }
      },
      [
        calcColumnContext,
        calcColumnIds,
        setCalcColumnToEdit,
        clearAndCancelPreview,
        getDisplayTableConfig,
        cellId,
        calcs?.calcColumns,
        setInProgressExpression,
        dispatchAO,
      ],
    );

    const queuedCalcUpdate = useRef<{
      expr: string;
      opts: {
        closeEditor?: boolean;
        runCell?: boolean;
        scrollToCalc?: boolean;
      };
    }>();

    const [fieldsToReplaceWithCalc, setFieldsToReplaceWithCalc] = useState<
      ExploreFieldId[] | undefined
    >(undefined);

    const updateCalc: TableCalcsContext["updateCalc"] = useCallback(
      (
        newColumnId,
        expression,
        {
          closeEditor = true,
          extraAOsAfterUpdate,
          runCell = false,
          scrollToCalc = true,
        } = {},
      ) => {
        const displayTableConfig = getDisplayTableConfig(cellId);
        if (displayTableConfig == null) {
          logErrorMsg("Error updating calcs", "No display table config found");
          return false;
        }

        if (!calcColumnContextProp && !calcs?.referencedColumns) {
          logErrorMsg(
            "Error updating calcs",
            "Cannot update calcs meaningfully without any column types",
          );
        }

        if (calcRenameInputOpen && queuedCalcUpdate.current == null) {
          // if the user was editing a calc and decided to enter renaming mode
          // queue up the update to be applied after the rename is done
          queuedCalcUpdate.current = {
            expr: expression,
            opts: { closeEditor, runCell },
          };
          return true;
        }

        const baseCalcs = calcs ?? EMPTY_CALCS;
        const calcColumnToEdit = calcColumnToEditRef.current;

        // If this update wouldn't actually change anything, then don't propagate it
        const noCalcChange =
          calcColumnToEdit === newColumnId &&
          baseCalcs.calcColumns.some(
            (c) => c.name === calcColumnToEdit && c.expression === expression,
          );
        if (noCalcChange) {
          if (closeEditor) {
            clearAndCancelPreview();
            setCurrentlyReferencedColumns_(stableEmptyArray());
            setCalcColumnToEdit(undefined);
            setInitialExpressionState(undefined);
            setFieldsToReplaceWithCalc(undefined);
            setInProgressCalcType(BaseCalcType.UNKNOWN);
            setDoesInProgressCalcHaveAgg(false);
          }
          return true;
        }

        const newCalcs: CalcsDefinition = produce(baseCalcs, (draft) => {
          const existingCalc = draft.calcColumns.find(
            (c) => c.name === calcColumnToEdit,
          );
          if (calcColumnToEdit != null && existingCalc != null) {
            existingCalc.expression = expression;

            // handle renaming the calc if necessary
            if (calcColumnToEdit !== newColumnId) {
              existingCalc.name = newColumnId;

              // any formulas referencing the old name should be updated to now reference the new name
              for (const col of draft.calcColumns) {
                if (col.name === calcColumnToEdit) continue;

                col.expression = updateCalcColumnReferences(
                  col.expression,
                  calcColumnToEdit,
                  newColumnId,
                );
              }
            }
          } else {
            draft.calcColumns.push({ name: newColumnId, expression });
          }

          const references = getReferencedTypesFromCalcs(
            draft.calcColumns,
            calcColumnContext,
            scopeGetter(),
            draft.referencedParamTypes,
          );
          draft.referencedColumns = references.referencedColumns;
          draft.referencedParamTypes = references.referencedParamTypes;
          normalizeCalcsDefinition(draft, true);
        });

        const maybeError = maybeGetCalcNameError(
          newCalcs.calcColumns,
          parsedTableOutput,
        );
        if (maybeError != null) {
          toaster.show({
            message: `Cannot save formula: ${maybeError}`,
            intent: "danger",
          });
          return false;
        }

        dispatchAO([
          ...getAOOperationsForCalcRename({
            ...getExploreSpec(cellId),
            prevColumnId: calcColumnToEdit,
            cellId,
            displayTableConfig,
            newColumnId,
          }),
          UPDATE_DISPLAY_TABLE_CONFIG.create({
            displayTableConfigId: displayTableConfig.id,
            cellId,
            key: "calcs",
            value: newCalcs,
            suppressAutoRun: !runCell,
          }),
          ...(extraAOsAfterUpdate ?? []),
        ]);

        clearAndCancelPreview();
        setFieldsToReplaceWithCalc(undefined);
        if (closeEditor) {
          setCalcColumnToEdit(undefined);
          setInitialExpressionState(undefined);
          setCurrentlyReferencedColumns_(stableEmptyArray());
        } else {
          if (calcColumnToEdit !== newColumnId) {
            setCalcColumnToEdit(newColumnId);
          }
        }

        setStale({ autoRun: runCell });

        if (scrollToCalc) {
          tableAPIRef.current?.ensureColumnVisible(newColumnId);
        }
        return true;
      },
      [
        calcRenameInputOpen,
        calcs,
        cellId,
        clearAndCancelPreview,
        dispatchAO,
        getDisplayTableConfig,
        getExploreSpec,
        parsedTableOutput,
        calcColumnContext,
        calcColumnContextProp,
        scopeGetter,
        setCalcColumnToEdit,
        setStale,
        toaster,
      ],
    );

    const updateCalcName: TableCalcsContext["updateCalcName"] = useCallback(
      (oldName, newName, runCell = true) => {
        const displayTableConfig = getDisplayTableConfig(cellId);
        if (displayTableConfig == null) {
          logErrorMsg("Error updating calcs", "No display table config found");
          return;
        }

        if (queuedCalcUpdate.current != null) {
          // if there's a calc update queued up, apply it that instead.
          // it handles renaming calc as well so we can exit early
          const { expr, opts } = queuedCalcUpdate.current;
          updateCalc(DisplayTableColumnId.check(newName), expr, opts);
          queuedCalcUpdate.current = undefined;
          return;
        }

        const colIdx =
          calcs?.calcColumns.findIndex((c) => c.name === oldName) ?? -1;
        if (colIdx === -1) {
          const updatingUnsavedCalc = calcColumnToEditRef.current === oldName;
          if (updatingUnsavedCalc) {
            setCalcColumnToEdit(DisplayTableColumnId.check(newName));
            // bring back focus to the editor after updating calc as we're in this block
            // for unsaved calc and the user probably wants to finish editing the calc
            editorAPIRef.current?.focus();
          }
          return;
        }

        if (calcs?.calcColumns == null || oldName === newName) return;

        const newColId = DisplayTableColumnId.check(newName);
        const finalCalcs: CalcsDefinition = produce(calcs, (draft) => {
          draft.calcColumns[colIdx].name = newColId;

          // any formulas referencing the old name should be updated to now reference the new name
          for (const col of draft.calcColumns) {
            if (col.name === newColId) continue;

            col.expression = updateCalcColumnReferences(
              col.expression,
              oldName,
              newName,
            );
          }
        });

        const maybeError = maybeGetCalcNameError(
          finalCalcs.calcColumns,
          parsedTableOutput,
        );
        if (maybeError != null) {
          toaster.show({
            message: `Cannot rename calculation column: ${maybeError}`,
            intent: "danger",
          });
          return;
        }

        dispatchAO([
          ...getAOOperationsForCalcRename({
            ...getExploreSpec(cellId),
            prevColumnId: oldName,
            cellId,
            displayTableConfig,
            newColumnId: newColId,
          }),
          UPDATE_DISPLAY_TABLE_CONFIG.create({
            displayTableConfigId: displayTableConfig.id,
            cellId,
            key: "calcs",
            value: finalCalcs,
            suppressAutoRun: !runCell,
          }),
        ]);
        setStale({ autoRun: runCell });
        clearAndCancelPreview();

        if (calcColumnToEditRef.current === oldName) {
          setCalcColumnToEdit(DisplayTableColumnId.check(newName));
        }
      },
      [
        getDisplayTableConfig,
        cellId,
        calcs,
        parsedTableOutput,
        dispatchAO,
        getExploreSpec,
        setStale,
        clearAndCancelPreview,
        updateCalc,
        setCalcColumnToEdit,
        toaster,
      ],
    );

    const setCalcRenameInputOpen = useCallback(
      (open: boolean) => {
        setCalcRenameInputOpen_(open);

        if (!open && queuedCalcUpdate.current != null) {
          // if the user closed the rename input, apply the queued calc update
          const { expr, opts } = queuedCalcUpdate.current;
          updateCalc(
            DisplayTableColumnId.check(calcColumnToEditRef.current),
            expr,
            opts,
          );
        }
        queuedCalcUpdate.current = undefined;
      },
      [updateCalc],
    );

    const deleteCalc: TableCalcsContext["deleteCalc"] = useCallback(
      (columnId, runCell = true) => {
        // remove the calc from the cell definition if it exists in there
        const operations = [];

        const displayTableConfig = getDisplayTableConfig(cellId);
        if (displayTableConfig == null) {
          logErrorMsg("Error updating calcs", "No display table config found");
          return;
        }

        if (calcs?.calcColumns != null) {
          const newCalcColumns = calcs.calcColumns.filter(
            (c) => c.name !== columnId,
          );

          if (newCalcColumns.length !== calcs.calcColumns.length) {
            const updatedCalcs: CalcsDefinition = produce(calcs, (draft) => {
              draft.calcColumns = castDraft(newCalcColumns);

              const references = getReferencedTypesFromCalcs(
                draft.calcColumns,
                calcColumnContext,
                scopeGetter(),
                draft.referencedParamTypes,
              );
              draft.referencedColumns = references.referencedColumns;
              draft.referencedParamTypes = references.referencedParamTypes;
              normalizeCalcsDefinition(draft, true);
            });

            const maybeError = maybeGetCalcNameError(
              updatedCalcs.calcColumns,
              parsedTableOutput,
            );
            if (maybeError != null) {
              toaster.show({
                message: `Cannot update calculations: ${maybeError}`,
                intent: "danger",
              });
              return;
            }

            operations.push(
              UPDATE_DISPLAY_TABLE_CONFIG.create({
                displayTableConfigId: displayTableConfig.id,
                cellId,
                key: "calcs",
                value: updatedCalcs,
                suppressAutoRun: !runCell,
              }),
            );

            if (
              columnId &&
              displayTableConfig?.customColumnOrdering?.includes(columnId)
            ) {
              operations.push(
                UPDATE_DISPLAY_TABLE_CONFIG.create({
                  displayTableConfigId: displayTableConfig.id,
                  cellId,
                  key: "customColumnOrdering",
                  value: displayTableConfig.customColumnOrdering.filter(
                    (col) => col !== columnId,
                  ),
                  suppressAutoRun: true,
                }),
              );
            }

            // clean up the explore spec if it exists
            const explore = getExploreSpec(cellId);
            if (explore.exploreSpec != null) {
              // If measure calc or used as measure filter - remove from filters
              const { resultTypes } = validateAllCalcsFromText(
                new Map(calcColumns.map((c) => [c.name, c.expression])),
                calcColumnContext,
                {},
                { aggregationMode: "groupable" },
              );
              const calcIsAgg = resultTypes.get(columnId)?.containsAggregate;
              const filtersToKeep = displayTableConfig.filters?.filter(
                (filter) => {
                  const filterOnThisCalcWithAgg =
                    filter.column === columnId && !!filter.aggregation;
                  const calcIsAggAndMatchesFilter =
                    calcIsAgg && filter.column === columnId;
                  return !filterOnThisCalcWithAgg && !calcIsAggAndMatchesFilter;
                },
              );
              if (
                filtersToKeep &&
                filtersToKeep.length !== displayTableConfig.filters?.length
              ) {
                operations.push(
                  UPDATE_DISPLAY_TABLE_CONFIG.create({
                    displayTableConfigId: displayTableConfig.id,
                    cellId,
                    key: "filters",
                    value: filtersToKeep,
                    suppressAutoRun: true,
                  }),
                );
              }

              const exploreDeleteCalc = getUpdateExploreSpecAOs({
                // Remove from viz and details
                actions: exploreSpecActions.removeCalc({
                  value: generateColumnIdForField({
                    value: columnId,
                    queryPath: [],
                  }),
                }),
                cellId,
                exploreCellId: explore.exploreCellId,
                origSpec: explore.exploreSpec,
                resultVariables: explore.resultVariables,
                suppressAutoRun: true,
              });
              operations.push(...exploreDeleteCalc);
            }

            const { opId } = dispatchAO(operations);
            if (!skipDeletedToast) {
              toaster.show(
                makeUndoToast({
                  message: `${columnId} removed`,
                  undoable: true,
                  onUndoClick: () => undoAO(opId),
                }),
              );
            }
          }
        }

        // if we were editing this calc, close the editor
        if (calcColumnToEditRef.current === columnId) {
          cancelEditingCalc();
        }
      },
      [
        getDisplayTableConfig,
        cellId,
        calcs,
        parsedTableOutput,
        getExploreSpec,
        dispatchAO,
        skipDeletedToast,
        calcColumnContext,
        scopeGetter,
        toaster,
        calcColumns,
        undoAO,
        cancelEditingCalc,
      ],
    );

    const columnClicked: TableCalcsContext["columnClicked"] = useCallback(
      (columnId) => {
        editorAPIRef.current?.addColumnReference(columnId);
      },
      [],
    );

    const totalClicked: TableCalcsContext["totalClicked"] = useCallback(
      (columnId, agg) => {
        editorAPIRef.current?.addTotalReference(columnId, agg);
      },
      [],
    );

    const initialExpression =
      initialExpressionState ??
      calcColumns.find((c) => c.name === calcColumnToEditState)?.expression;

    const context = useMemo(
      (): TableCalcsContext => ({
        calcColumns,
        calcColumnToEdit: calcColumnToEditState,
        columnClicked,
        totalClicked,
        editorAPIRef,
        tableAPIRef,
        currentlyReferencedColumns,
        referencedParamTypes,
        calcColumnContext,
        previewData,
        getIsEditingCalcColumn,
        setInProgressExpression,
        startEditingNewCalc,
        startEditingExistingCalc,
        cancelEditingCalc,
        updateCalc,
        updateCalcName,
        deleteCalc,
        calcRenameInputOpen,
        setCalcRenameInputOpen,
        setIsInProgressCalcValid,
        initialExpression,
        isInProgressCalcValid,
        isAuthoringExploreMeasure,
        inProgressCalcType,
        doesInProgressCalcHaveAgg,
        fieldsToReplaceWithCalc,
        isEditingCalc,
        setCurrentlyReferencedColumns,
      }),
      [
        calcColumns,
        calcColumnToEditState,
        columnClicked,
        totalClicked,
        currentlyReferencedColumns,
        referencedParamTypes,
        calcColumnContext,
        previewData,
        getIsEditingCalcColumn,
        setInProgressExpression,
        startEditingNewCalc,
        startEditingExistingCalc,
        cancelEditingCalc,
        updateCalc,
        updateCalcName,
        deleteCalc,
        calcRenameInputOpen,
        setCalcRenameInputOpen,
        initialExpression,
        isInProgressCalcValid,
        isAuthoringExploreMeasure,
        inProgressCalcType,
        doesInProgressCalcHaveAgg,
        fieldsToReplaceWithCalc,
        isEditingCalc,
        setCurrentlyReferencedColumns,
      ],
    );
    return <Context.Provider value={context}>{children}</Context.Provider>;
  });

export const useTableCalcsContext = (): TableCalcsContext => {
  const maybeContext = useContext(Context);
  if (maybeContext == null) {
    throw new Error(
      "Tried to access TableCalcsContext in a tree where it wasn't provided.",
    );
  }
  return maybeContext;
};

// TableCalcContext is used to power calc-editing features throughout the table,
// but isn't always available in non-editing contexts like a published app.
// To make sure code doesn't try to read info from here in those scenarios,
// (it should instead just look at info on the cell contents directly) , omit `calcColumns` here.
export const useMaybeTableCalcsContext = (): Omit<
  TableCalcsContext,
  "calcColumns"
> | null => {
  return useContext(Context);
};

// If calc name is changed, since we're changing the name of the column of the output dataframe
// and not just renaming it in the UI like for non-calc column renames, we need to update all
// the fields in the dtc that reference this column
function getAOOperationsForCalcRename({
  cellId,
  displayTableConfig,
  exploreCellId,
  exploreSpec,
  newColumnId,
  prevColumnId,
}: {
  displayTableConfig: DisplayTableConfigFragment;
  cellId: CellId;
  prevColumnId: DisplayTableColumnId | undefined;
  newColumnId: DisplayTableColumnId;
  exploreSpec: ExploreSpec | null;
  exploreCellId: ExploreCellId | null;
}): HexVersionAtomicOperation[] {
  if (prevColumnId == null || prevColumnId === newColumnId) {
    return [];
  }

  const displayTableConfigId = displayTableConfig.id;
  const operations: HexVersionAtomicOperation[] = [];

  if (displayTableConfig.sortByColumnDefault === prevColumnId) {
    operations.push(
      UPDATE_DISPLAY_TABLE_CONFIG.create({
        displayTableConfigId,
        cellId,
        key: "sortByColumnDefault",
        value: newColumnId,
        suppressAutoRun: true,
      }),
    );
  }

  if (displayTableConfig.conditionalFormatting != null) {
    let updatedRulesets = false;
    const newConditionalFormatting = produce(
      displayTableConfig.conditionalFormatting,
      (draft) => {
        draft.rulesets.forEach((ruleset) => {
          if (ruleset.columns.type === "INCLUDE") {
            ruleset.columns.includedColumns =
              ruleset.columns.includedColumns.map((column) => {
                if (column === prevColumnId) {
                  updatedRulesets = true;
                  return newColumnId;
                }
                return column;
              });
          } else if (ruleset.columns.type === "EXCLUDE") {
            ruleset.columns.excludedColumns =
              ruleset.columns.excludedColumns.map((column) => {
                if (column === prevColumnId) {
                  updatedRulesets = true;
                  return newColumnId;
                }
                return column;
              });
          }
        });
      },
    );

    if (updatedRulesets) {
      operations.push(
        UPDATE_DISPLAY_TABLE_CONFIG.create({
          displayTableConfigId,
          cellId,
          key: "conditionalFormatting",
          value: newConditionalFormatting,
          suppressAutoRun: true,
        }),
      );
    }
  }

  if (displayTableConfig.filters != null) {
    let updatedFilters = false;
    const newFilters = produce(displayTableConfig.filters, (draft) => {
      draft.forEach((filter) => {
        if (filter.column === prevColumnId) {
          updatedFilters = true;
          filter.column = newColumnId;
        }
      });
    });

    if (updatedFilters) {
      operations.push(
        UPDATE_DISPLAY_TABLE_CONFIG.create({
          displayTableConfigId,
          cellId,
          key: "filters",
          value: newFilters,
          suppressAutoRun: true,
        }),
      );
    }
  }

  const existingColumnProps = displayTableConfig.columnProperties.find(
    (p) => p.originalName === prevColumnId,
  );
  if (existingColumnProps != null) {
    for (const key of UpdateColumnPropertiesSafeFields) {
      // we don't allow UI-based renaming of calcs columns, so skip this field
      if (key === "renameTo") continue;

      operations.push(
        UPDATE_COLUMN_PROPERTIES.create({
          displayTableConfigId,
          cellId,
          originalName: newColumnId,
          key,
          value: existingColumnProps[key],
        }),
      );
    }
  }

  if (displayTableConfig.pinnedColumns?.includes(prevColumnId)) {
    operations.push(
      UPDATE_DISPLAY_TABLE_CONFIG.create({
        displayTableConfigId,
        cellId,
        key: "pinnedColumns",
        value: displayTableConfig.pinnedColumns.map((col) =>
          col === prevColumnId ? newColumnId : col,
        ),
        suppressAutoRun: true,
      }),
    );
  }

  if (displayTableConfig.hiddenColumns?.includes(prevColumnId)) {
    operations.push(
      UPDATE_DISPLAY_TABLE_CONFIG.create({
        displayTableConfigId,
        cellId,
        key: "hiddenColumns",
        value: displayTableConfig.hiddenColumns.map((col) =>
          col === prevColumnId ? newColumnId : col,
        ),
        suppressAutoRun: true,
      }),
    );
  }

  if (displayTableConfig.customColumnOrdering?.includes(prevColumnId)) {
    operations.push(
      UPDATE_DISPLAY_TABLE_CONFIG.create({
        displayTableConfigId,
        cellId,
        key: "customColumnOrdering",
        value: displayTableConfig.customColumnOrdering.map((col) =>
          col === prevColumnId ? newColumnId : col,
        ),
        suppressAutoRun: true,
      }),
    );
  }

  if (exploreCellId && exploreSpec != null) {
    const action = exploreSpecActions.updateCalcName({
      currName: prevColumnId,
      newName: newColumnId,
    });
    const newSpec = exploreSpecReducer(exploreSpec, action);
    operations.push(
      UPDATE_EXPLORE_CELL.create({
        cellId,
        exploreCellId,
        key: "spec",
        value: newSpec,
        suppressAutoRun: true,
      }),
    );
  }

  return operations;
}

export const baseCalcTypeToDisplayTableColumnOutputType = (
  calcType: BaseCalcType,
): DisplayTableColumnOutputType => {
  switch (calcType) {
    case BaseCalcType.NUMBER:
      return "NUMBER";
    case BaseCalcType.STRING:
      return "STRING";
    case BaseCalcType.BOOLEAN:
      return "BOOLEAN";
    case BaseCalcType.DATETIME:
      return "DATETIME";
    case BaseCalcType.TIME:
      return "TIME";
    case BaseCalcType.NULL:
      return "UNKNOWN";
    case BaseCalcType.UNKNOWN:
      return "UNKNOWN";
    default:
      assertNever(calcType, calcType);
  }
};

const getTypeAndErrorOfDraftCalc = (
  calcColumns: readonly CalcColumnDefinition[],
  calcColumnContext: CalcColumnContext,
  initialExpressionParam: string,
  newColumnName: string,
) => {
  const existingCalcs = new Map<string, string>(
    calcColumns.map((c) => [c.name, c.expression]) ?? [],
  );
  existingCalcs.set(newColumnName, initialExpressionParam);
  const { errors: calcErrors, resultTypes } = validateAllCalcsFromText(
    existingCalcs,
    calcColumnContext,
    {},
    // TODO: customize
    { aggregationMode: "groupable" },
  );
  return {
    resultType: resultTypes.get(newColumnName),
    errors: calcErrors.get(newColumnName),
  };
};
